/**
 * Scroll Animations — Frontend Observer
 *
 * 1. Watches [data-animate] elements and adds .is-visible on intersection.
 * 2. Animates star ratings — stars start gray, fill in with color one by one
 *    when they scroll into view. Works on ratingSummary and individual review cards.
 *
 * @package Interact_26
 */

const SELECTOR = '[data-animate]';
const DEFAULT_THRESHOLD = 0.15;

const STAR_EMPTY = '#DFDFE2';
const STAR_STAGGER = 250;

// Only animate stars in the rating summary, not individual review cards
const STAR_SELECTOR = '[class*="ratingSummary"] [aria-label*="out of 5 stars"]';

/**
 * Store original fill on each star path, then set to gray via inline style.
 * Using style.fill (not setAttribute) so CSS transitions work.
 */
function prepareStarContainer(container: HTMLElement): void {
  container.querySelectorAll<SVGPathElement>('svg path').forEach((path) => {
    path.dataset.originalFill = path.getAttribute('fill') || STAR_EMPTY;
    path.style.fill = STAR_EMPTY;
  });
}

/**
 * Fill stars one by one with their original color, left to right.
 */
function revealStars(container: HTMLElement): void {
  const svgs = Array.from(container.querySelectorAll('svg'));

  svgs.forEach((svg, i) => {
    setTimeout(() => {
      svg.querySelectorAll<SVGPathElement>('path').forEach((path) => {
        path.style.fill = path.dataset.originalFill || STAR_EMPTY;
      });
    }, i * STAR_STAGGER);
  });
}

function init(): void {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Block scroll animations ---
  if (reducedMotion) {
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
      el.classList.add('is-visible');
    });
  } else {
    const thresholdMap = new Map<number, HTMLElement[]>();

    document.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
      const raw = el.dataset.animateThreshold;
      const threshold = raw ? parseInt(raw, 10) / 100 : DEFAULT_THRESHOLD;
      const clamped = Math.max(0.05, Math.min(0.5, threshold));

      if (!thresholdMap.has(clamped)) {
        thresholdMap.set(clamped, []);
      }
      thresholdMap.get(clamped)!.push(el);
    });

    thresholdMap.forEach((elements, threshold) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold },
      );

      elements.forEach((el) => observer.observe(el));
    });
  }

  // --- Star rating fill animation (standalone, no data-animate needed) ---
  const starContainers = document.querySelectorAll<HTMLElement>(STAR_SELECTOR);

  if (starContainers.length === 0 || reducedMotion) return;

  // Set all stars to gray
  starContainers.forEach(prepareStarContainer);

  // Observe each star container independently
  const starObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTimeout(() => revealStars(entry.target as HTMLElement), 300);
          starObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.3 },
  );

  starContainers.forEach((el) => starObserver.observe(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
